import {useQuery} from '@tanstack/react-query';
import {DaysLeft} from '@app/opportunities/days-left';
import {Link} from 'react-router';
import {Button} from '@ui/buttons/button';
import {Trans} from '@ui/i18n/trans';
import {apiClient} from '@common/http/query-client';
import {getBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';
import {useSelectedLocale} from '@ui/i18n/selected-locale';
import type {BlockInstance} from '../appearance-data';
import {EyebrowPill} from './eyebrow-pill';
import {Reveal} from '../motion';
import {Carousel} from '../carousel';
import {Section} from './section-kit';
import {accent} from './accents';
import {FavoriteButton} from '@app/opportunities/favorite-button';
import {SafeImage} from '@app/opportunities/safe-image';
import {FavoriteTarget} from '@app/opportunities/favorites-queries';
import {displayCountryName} from '@common/ui/country/country-display';

interface Card {
  id?: number;
  type?: string;
  slug?: string | null;
  title: string;
  image: string | null;
  cover?: string | null;
  country: string | null;
  provider: string | null;
  badge?: string | null;
  is_new?: boolean;
  meta?: string | null;
  deadline?: string | null;
  days_left?: number | null;
  url: string;
}

/** Per-card display toggles, chosen in the block settings. */
interface Display {
  showLogo: boolean;
  showBadge: boolean;
  showCountry: boolean;
  cta: 'none' | 'days_left' | 'apply';
}

/** The card's optional bottom CTA row: nothing, a days-left countdown, or an
 *  Apply-now button. Rendered as plain elements (no nested <a>) because the
 *  whole card is already a Link — clicking anywhere goes to the detail page. */
function CardCta({display, card}: {display: Display; card: Card}) {
  if (display.cta === 'apply') {
    return (
      <span className="ap-shine mt-12 inline-flex items-center justify-center gap-4 rounded-full bg-primary px-16 py-8 text-xs font-bold text-white shadow-sm shadow-primary/25">
        <Trans message="Apply Now" />
        <span className="material-icons text-sm">arrow_forward</span>
      </span>
    );
  }
  if (display.cta === 'days_left' && card.days_left != null) {
    return (
      <p className="mt-10 flex items-center gap-4 border-t border-divider pt-10 text-xs font-semibold text-danger">
        <span className="material-icons text-sm">schedule</span>
        <DaysLeft days={card.days_left} />
      </p>
    );
  }
  // cta === 'none' → fall back to the plain deadline line (if any).
  if (display.cta === 'none' && card.deadline) {
    return (
      <p className="mt-10 flex items-center gap-4 border-t border-divider pt-10 text-xs font-medium text-danger">
        <span className="material-icons text-sm">event</span>
        <Trans message="Deadline" />: {card.deadline}
      </p>
    );
  }
  return null;
}

/** Heart toggle for a featured card, if the feed carried an id + type. */
function cardFavorite(c: Card): FavoriteTarget | null {
  if (!c.id || !c.type) return null;
  return {
    listing_type: c.type as FavoriteTarget['listing_type'],
    listing_id: c.id,
    listing_slug: c.slug ?? null,
    listing_title: c.title,
    listing_logo: c.image ?? c.cover ?? null,
    listing_country: c.country,
  };
}

/** Localizes an ISO country code ("CN" → "China" / "الصين").
 *
 * Delegates to the shared `displayCountryName`, which applies NAME_OVERRIDES
 * before falling back to Intl.DisplayNames. That matters: some devices ship a
 * newer ICU where `CN` resolves to "China mainland" (Apple/Safari especially),
 * so calling Intl directly here produced a different country name per device.
 * The override table is the single source of truth — don't call Intl directly. */
function useCountryName() {
  const {localeCode} = useSelectedLocale();
  // Base part only ("en-US" → "en") so the override table lookup hits.
  const loc = String(localeCode || 'en').toLowerCase().split('-')[0];
  return (code?: string | null) => {
    if (!code) return null;
    const c = code.trim().toUpperCase();
    // Only 2-letter ISO codes resolve; anything else is already a name/provider.
    if (c.length !== 2) return code;
    return displayCountryName(c, code, loc) || code;
  };
}

/** Flag + localized country name, shown consistently on every card. */
function CountryTag({
  code,
  name,
  baseUrl,
}: {
  code?: string | null;
  name?: string | null;
  baseUrl: string;
}) {
  if (!code) return name ? <span className="truncate">{name}</span> : null;
  const iso = code.trim().toLowerCase();
  const flag = iso.length === 2 ? `${baseUrl}/flags/${iso}.svg` : null;
  return (
    <span className="flex min-w-0 items-center gap-5">
      {flag ? (
        <SafeImage sources={[flag]} className="h-12 w-18 shrink-0 rounded-sm object-cover ring-1 ring-black/5" />
      ) : null}
      <span className="truncate">{name || code}</span>
    </span>
  );
}

/**
 * Data-backed "Featured …" block. The opportunity type comes from the block
 * (feature_type setting, or derived from the block key). Fetches a small live
 * feed from /appearance/featured and renders cards. Used by all four featured
 * blocks (scholarships / universities / courses / jobs).
 */
export function FeaturedBlock({block, props}: {block: BlockInstance; props: Record<string, any>}) {
  const type = props.feature_type || block.type.replace('featured-', '').replace(/s$/, '');
  const limit = parseInt(props.limit || '6', 10);
  const source = props.source || 'featured';
  const ids = source === 'selected' ? String(props.pick_ids || '').trim() : '';
  const cols = props.columns === '4' ? 'md:grid-cols-4' : props.columns === '2' ? 'md:grid-cols-2' : 'md:grid-cols-3';
  const baseUrl = getBootstrapData().settings.base_url || '';
  const imageStyle = props.card_style === 'image';
  const countryName = useCountryName();
  const {localeCode} = useSelectedLocale();
  // Per-card display toggles (default ON, except the CTA which defaults off).
  const display: Display = {
    showLogo: props.show_logo !== false,
    showBadge: props.show_badge !== false,
    showCountry: props.show_country !== false,
    cta: (props.card_cta as Display['cta']) || 'none',
  };

  const {data} = useQuery({
    // localeCode is part of the key + sent as `lang` so the listing TITLES
    // re-fetch and re-localize when the user switches language (the API
    // resolves titles server-side per the active locale, which SetAppLocale
    // reads from ?lang=). Without this the names stayed in the first locale.
    queryKey: ['featured', type, limit, source, ids, localeCode],
    queryFn: () =>
      apiClient
        .get('appearance/featured', {params: {type, limit, source, ids, lang: localeCode}})
        .then(r => r.data.items as Card[]),
    staleTime: 5 * 60 * 1000,
  });

  const items = data ?? [];
  if (!items.length) return null;

  // `reveal` only in the grid — inside a horizontally scrolling carousel a
  // scroll-triggered reveal would leave off-screen slides invisible.
  const renderCard = (c: Card, i: number, reveal: boolean) => {
    const card = imageStyle ? (
      <ImageCard card={c} baseUrl={baseUrl} index={i} countryName={countryName} display={display} />
    ) : (
      <LogoCard card={c} baseUrl={baseUrl} countryName={countryName} display={display} />
    );
    return reveal ? <Reveal key={i} index={i}>{card}</Reveal> : <div key={i} className="h-full">{card}</div>;
  };

  return (
    <Section index={props.__index} bgStyle={props.bg_style} bgColor={props.bg_color} className="!py-56">
      <div className="mx-auto max-w-[1100px]">
        <EyebrowPill text={props.eyebrow} align="start" />
        <div className="mb-24 flex items-center justify-between gap-16">
          {props.heading ? <h2 className="text-2xl font-bold md:text-3xl">{props.heading}</h2> : <span />}
          {props.view_all_label ? (
            <Button variant="text" color="primary" size="sm" elementType={Link} to={props.view_all_url || '/opportunities'}>
              {props.view_all_label}
            </Button>
          ) : null}
        </div>
        {/* One card renderer for both layouts, so EVERY combination of
            card_style × layout works (carousel used to be silently ignored for
            logo cards — picking it just re-rendered the grid). */}
        {/* Desktop card width is computed so exactly 3 cards + 2×20px gaps fill
            the track. Fixed 340px cards overflowed the (container − 2×48px arrow
            gutter) width, so the third card was always clipped. */}
        {props.layout === 'carousel' ? (
          <Carousel
            itemClass="w-[280px] sm:w-[320px] md:w-[calc((100%-40px)/3)]"
            ariaLabel={props.heading}
          >
            {items.map((c, i) => renderCard(c, i, false))}
          </Carousel>
        ) : (
          <div className={`grid grid-cols-1 gap-24 sm:grid-cols-2 ${cols}`}>
            {items.map((c, i) => renderCard(c, i, true))}
          </div>
        )}
      </div>
    </Section>
  );
}

/** "New" ribbon for recently-added listings. Position is passed in so it can
 *  dodge the favorite heart (top-right) and, on image cards, the funding badge
 *  (top-left) — defaults to top-left for the plain logo card. */
function NewBadge({position = 'start-10 top-10'}: {position?: string}) {
  return (
    <span className={`absolute ${position} z-10 rounded-full bg-positive px-8 py-2 text-2xs font-bold uppercase tracking-wide text-white shadow-lg`}>
      <Trans message="New" />
    </span>
  );
}

/** Favorite heart, pinned to the card's TOP-RIGHT corner. */
function CardHeart({card}: {card: Card}) {
  const fav = cardFavorite(card);
  if (!fav) return null;
  return (
    <div className="absolute end-8 top-8 z-20">
      <FavoriteButton target={fav} size="sm" onSurface />
    </div>
  );
}

/** Compact logo card (logo on a tinted chip, title, localized country row). */
function LogoCard({
  card: c,
  baseUrl,
  countryName,
  display,
}: {
  card: Card;
  baseUrl: string;
  countryName: (code?: string | null) => string | null;
  display: Display;
}) {
  const country = countryName(c.country);
  return (
    <Link
      to={c.url}
      className="group relative flex flex-col overflow-hidden rounded-2xl border border-divider bg-paper transition hover:border-primary hover:shadow-md"
    >
      {c.is_new ? <NewBadge /> : null}
      <CardHeart card={c} />
      {display.showLogo ? (
        <div className="flex h-120 items-center justify-center bg-alt p-16">
          <SafeImage
            sources={[c.image]}
            className="max-h-full max-w-full object-contain"
            fallback={<span className="material-icons text-4xl text-muted">school</span>}
          />
        </div>
      ) : null}
      <div className="flex flex-1 flex-col p-16">
        <h3 className="line-clamp-2 font-semibold group-hover:text-primary">{c.title}</h3>
        {display.showCountry ? (
          <div className="mt-auto flex items-center gap-8 pt-12 text-xs text-muted">
            <CountryTag code={c.country} name={country} baseUrl={baseUrl} />
            {c.provider && c.country ? <span className="truncate opacity-70">· {c.provider}</span> : null}
            {c.provider && !c.country ? <span className="truncate">{c.provider}</span> : null}
          </div>
        ) : null}
        <CardCta display={display} card={c} />
      </div>
    </Link>
  );
}

/**
 * Rich "Popular Opportunities" card: cover image banner with a funding badge
 * overlay, title, country + study level, and a deadline footer. Falls back to
 * the logo (or a tinted placeholder) when no cover image is available.
 */
function ImageCard({
  card: c,
  baseUrl,
  index = 0,
  countryName,
  display,
}: {
  card: Card;
  baseUrl: string;
  index?: number;
  countryName: (code?: string | null) => string | null;
  display: Display;
}) {
  const cover = c.cover || c.image;
  // Only a true cover photo is worth showing full-bleed. A provider *logo*
  // stretched to fill looks broken, so it gets the designed fallback instead.
  const hasPhoto = !!c.cover;
  const country = countryName(c.country);
  const a = accent(index);
  return (
    <Link
      to={c.url}
      className="ap-lift ap-zoom ap-shine group relative flex h-full flex-col overflow-hidden rounded-2xl border border-divider bg-paper shadow-sm transition hover:border-primary hover:shadow-xl"
    >
      <div className="relative h-180 overflow-hidden" style={{backgroundColor: a.bg}}>
        {hasPhoto ? (
          // Third-party covers 404 often — fall through to the logo, then to
          // the designed placeholder, rather than showing a broken image.
          <SafeImage
            sources={[cover, c.image]}
            className="h-full w-full object-cover"
            fallback={
              <div className="flex h-full items-center justify-center">
                <span className="material-icons text-5xl" style={{color: a.fg}}>school</span>
              </div>
            }
          />
        ) : (
          // No photo on this dataset (scholarships/courses only carry a
          // provider logo) — render a designed tile: tinted ground, soft
          // radial wash, and the logo centred on a white chip.
          <div
            className="flex h-full items-center justify-center"
            style={{
              backgroundImage: `radial-gradient(circle at 30% 20%, ${a.fg}22, transparent 60%), radial-gradient(circle at 80% 90%, ${a.fg}18, transparent 55%)`,
            }}
          >
            {display.showLogo && c.image ? (
              <span className="flex h-72 w-72 items-center justify-center rounded-2xl bg-white/90 p-10 shadow-md ring-1 ring-black/5">
                <SafeImage sources={[c.image]} className="max-h-full max-w-full object-contain"
                  fallback={<span className="material-icons text-3xl" style={{color: a.fg}}>school</span>} />
              </span>
            ) : (
              <span className="material-icons text-5xl" style={{color: a.fg}}>
                school
              </span>
            )}
          </div>
        )}
        {/* Soft scrim under the badge/edge so photos never wash out the chrome. */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-black/10" />
        {/* Corner budget on the banner: funding badge top-start, heart top-end,
            logo chip bottom-start, "New" bottom-end — so nothing ever stacks. */}
        {c.is_new ? <NewBadge position="bottom-8 end-8" /> : null}
        {display.showBadge && c.badge ? (
          <span className="absolute start-10 top-10 rounded-full bg-primary px-10 py-2 text-2xs font-semibold text-white shadow-lg">
            {/* English seed from the API — must go through Trans to localize. */}
            <Trans message={c.badge} />
          </span>
        ) : null}
        {/* Heart on the banner's top-right corner. */}
        {cardFavorite(c) ? (
          <div className="absolute end-8 top-8 z-20">
            <FavoriteButton target={cardFavorite(c)!} size="sm" onSurface />
          </div>
        ) : null}
        {/* Listing logo as a white chip in the banner's bottom-start corner.
            Previously the logo was only a FALLBACK when no cover photo existed,
            so photo-led cards showed no brand at all. Kept INSIDE the banner
            because the wrapper is overflow-hidden (an overhang would clip). */}
        {display.showLogo && hasPhoto && c.image ? (
          <span className="absolute bottom-8 start-8 z-20 flex h-40 w-40 items-center justify-center rounded-xl bg-white p-5 shadow-lg ring-1 ring-black/10">
            <SafeImage sources={[c.image]} className="max-h-full max-w-full object-contain" />
          </span>
        ) : null}
      </div>
      <div className="flex flex-1 flex-col p-16">
        <h3 className="line-clamp-2 font-semibold group-hover:text-primary">{c.title}</h3>
        {display.showCountry || c.meta ? (
          <div className="mt-8 flex flex-wrap items-center gap-x-12 gap-y-4 text-xs text-muted">
            {display.showCountry ? (
              <>
                <CountryTag code={c.country} name={country} baseUrl={baseUrl} />
                {c.provider && c.country ? <span className="truncate opacity-70">{c.provider}</span> : null}
                {c.provider && !c.country ? <span className="truncate">{c.provider}</span> : null}
              </>
            ) : null}
            {c.meta ? (
              <span className="flex items-center gap-3">
                <span className="material-icons text-sm">school</span>
                {c.meta}
              </span>
            ) : null}
          </div>
        ) : null}
        <CardCta display={display} card={c} />
      </div>
    </Link>
  );
}
